import { FormField, Switch } from '@george-labs.com/design-system';
import type { FORM_FIELD_LABEL_POSITION } from '@george-labs.com/design-system/types/components/Form';
import type { ComponentProps } from 'react';

import { useField } from '@luna/form2';

import { shouldShowError } from './FieldErrorVisibilityService';
import type { FormFieldProps } from './types/FormFieldProps';

interface Props<FormValues> extends FormFieldProps<FormValues> {
    inputProps?: Partial<ComponentProps<typeof Switch>>;
    isErrorVisible?: boolean;
    shouldClearOnUnmount?: boolean;
}

interface FormSwitchFieldStatics {
    LABEL_POSITION: typeof FORM_FIELD_LABEL_POSITION;
}

const LABEL_COLUMN_WIDTH = 4;

export function FormSwitchField<FormValues = void>({
    'data-cy': dataCy,
    fieldName,
    label,
    labelPosition,
    labelColumnWidth = LABEL_COLUMN_WIDTH,
    labelClassName,
    warning,
    info,
    alert,
    description,
    disabled,
    required = false,
    inputProps,
    isErrorVisible = true,
    addon,
    shouldClearOnUnmount,
    showErrorBeforeBlur,
}: Props<FormValues>) {
    const [fieldProps, { touched, error, dirty }] = useField<boolean>(fieldName, {
        type: 'checkbox',
        shouldClearOnUnmount,
    });

    const isFieldErrorVisible = shouldShowError(
        isErrorVisible,
        touched,
        error,
        showErrorBeforeBlur,
        dirty,
    );

    return (
        <FormField
            input={Switch}
            inputProps={{
                ...fieldProps,
                ...inputProps,
                'data-cy': dataCy,
                active: fieldProps.value,
                onToggle: (isActive: boolean) => {
                    fieldProps.onChange({
                        target: {
                            value: isActive,
                        },
                    });
                    inputProps?.onToggle?.(isActive);
                },
            }}
            label={label}
            labelClassName={labelClassName}
            labelPosition={labelPosition}
            labelColumnWidth={labelColumnWidth}
            error={isFieldErrorVisible && error}
            warning={warning}
            info={info}
            alert={alert}
            description={description}
            required={required}
            disabled={disabled}
            addon={addon}
        />
    );
}

export type FormSwitchField = typeof FormSwitchField & FormSwitchFieldStatics;

FormSwitchField.LABEL_POSITION = FormField.LABEL_POSITION;
